import { el } from "./dom.js";

/**
 * Global room-invite popup. Lives on document.body at a high z-index so it
 * shows over ANY screen (menu, lobby, match) and never fights the game HUD.
 * Only one is visible at a time; a new invite replaces the old.
 */

let current: HTMLElement | null = null;

export function showInvite(from: string, onAccept: () => void, onDecline: () => void): void {
  current?.remove();
  const wrap = el("div", {
    "data-pad-modal": "", // pad-nav scopes to this popup while it is up (menus only)
    style:
      "position:fixed;top:70px;left:50%;transform:translateX(-50%);z-index:2000;" +
      "background:rgba(8,12,20,0.96);border:1px solid #00f0ff;box-shadow:0 0 24px #00f0ff55;" +
      "padding:16px 22px;min-width:320px;text-align:center;font-family:inherit;letter-spacing:0.05em",
  });
  const accept = el("button", { class: "primary", text: "✔ Join", style: "min-width:110px" });
  const decline = el("button", { class: "ghost", text: "Decline", "data-pad-back": "Decline", style: "min-width:110px;margin-left:10px" });
  let done = false;
  const close = (): void => { if (current === wrap) current = null; wrap.remove(); };
  accept.onclick = () => { if (done) return; done = true; close(); onAccept(); };
  decline.onclick = () => { if (done) return; done = true; close(); onDecline(); };
  wrap.append(
    el("div", { style: "color:#ff2fd0;font-size:0.8em;margin-bottom:2px", text: "ROOM INVITE" }),
    el("div", { style: "color:#e8f6ff;font-size:1.15em;margin-bottom:12px" },
      el("b", { text: from }), el("span", { text: " invites you to their room" })),
    el("div", { style: "display:flex;justify-content:center" }, accept, decline),
  );
  document.body.append(wrap);
  current = wrap;
  // auto-decline after 30 s so a stale invite doesn't linger forever
  setTimeout(() => { if (!done && current === wrap) { done = true; close(); onDecline(); } }, 30_000);
}

export function clearInvite(): void {
  current?.remove();
  current = null;
}


/** Friend-request popup (same slot as invites; Accept adds them as a friend). */
export function showFriendRequest(from: string, onAccept: () => void, onDecline: () => void): void {
  current?.remove();
  const wrap = el("div", {
    "data-pad-modal": "", // pad-nav scopes to this popup while it is up (menus only)
    style:
      "position:fixed;top:70px;left:50%;transform:translateX(-50%);z-index:2000;" +
      "background:rgba(8,12,20,0.96);border:1px solid #ff2fd0;box-shadow:0 0 24px #ff2fd055;" +
      "padding:16px 22px;min-width:320px;text-align:center;font-family:inherit;letter-spacing:0.05em",
  });
  const accept = el("button", { class: "primary", text: "Accept", style: "min-width:110px" });
  const decline = el("button", { class: "ghost", text: "Decline", "data-pad-back": "Decline", style: "min-width:110px;margin-left:10px" });
  let done = false;
  const close = (): void => { if (current === wrap) current = null; wrap.remove(); };
  accept.onclick = () => { if (done) return; done = true; close(); onAccept(); };
  decline.onclick = () => { if (done) return; done = true; close(); onDecline(); };
  wrap.append(
    el("div", { style: "color:#ff2fd0;font-size:0.8em;margin-bottom:2px", text: "FRIEND REQUEST" }),
    el("div", { style: "color:#e8f6ff;font-size:1.15em;margin-bottom:12px" },
      el("b", { text: from }), el("span", { text: " wants to be your friend" })),
    el("div", { style: "display:flex;justify-content:center" }, accept, decline),
  );
  document.body.append(wrap);
  current = wrap;
  setTimeout(() => { if (!done && current === wrap) { done = true; close(); onDecline(); } }, 30_000);
}

/** First-run welcome: shown once per device after the first login / guest
 *  connect (gated by a localStorage flag in main.ts). Offers the tutorial.
 *  Uses its own slot so an incoming invite can't clobber it. */
let welcomeEl: HTMLElement | null = null;
export function showWelcome(onYes: () => void, onNo: () => void): void {
  welcomeEl?.remove();
  const wrap = el("div", {
    "data-pad-modal": "", // pad-nav scopes to this popup while it is up (menus only)
    style:
      "position:fixed;top:70px;left:50%;transform:translateX(-50%);z-index:2100;" +
      "background:rgba(8,12,20,0.97);border:1px solid #4dff9e;box-shadow:0 0 24px #4dff9e55;" +
      "padding:18px 24px;min-width:320px;max-width:90vw;text-align:center;font-family:inherit;letter-spacing:0.05em",
  });
  const yes = el("button", { class: "primary", text: "✔ Yes, show me", style: "min-width:130px" });
  const no = el("button", { class: "ghost", text: "No thanks", "data-pad-back": "No thanks", style: "min-width:110px;margin-left:10px" });
  let done = false;
  const close = (): void => { if (welcomeEl === wrap) welcomeEl = null; wrap.remove(); };
  yes.onclick = () => { if (done) return; done = true; close(); onYes(); };
  no.onclick = () => { if (done) return; done = true; close(); onNo(); };
  wrap.append(
    el("div", { style: "color:#4dff9e;font-size:0.8em;margin-bottom:2px", text: "WELCOME" }),
    el("div", { style: "color:#e8f6ff;font-size:1.15em;margin-bottom:6px" },
      el("b", { text: "You're new here 👋" })),
    el("div", { style: "color:#a9c4d6;font-size:0.95em;margin-bottom:14px", text: "Want a quick tutorial to learn the ropes?" }),
    el("div", { style: "display:flex;justify-content:center;flex-wrap:wrap;gap:8px" }, yes, no),
  );
  document.body.append(wrap);
  welcomeEl = wrap;
}

/** Dismiss the "take the training" welcome popup (e.g. when a game launches). */
export function clearWelcome(): void {
  welcomeEl?.remove();
  welcomeEl = null;
}